import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { fetchResourcesAPI, updateResourcesAPI } from '../../services/api/resourcesService';
import { ResourceDetails, ResourceDetailsMutation } from '../../types/resources/resources.types';
import { RootState } from '../store';

// Define the state interface
interface ResourcesState {
  resources: ResourceDetails[];
  selectedResource: ResourceDetails | null;
  isLoading: boolean;
  error: string | null;
  hasAttemptedFetch: boolean;
}

// Initial state
const initialState: ResourcesState = {
  resources: [],
  selectedResource: null,
  isLoading: false,
  error: null,
  hasAttemptedFetch: false,
};

// Async thunk actions using the helper function for consistent error handling
export const fetchResources = createAsyncThunk('resources/fetchResources', async (_, { rejectWithValue }) => {
  try {
    return await fetchResourcesAPI();
  } catch (error) {
    return rejectWithValue((error as Error).message || 'Failed to fetch resources');
  }
});

export const updateResources = createAsyncThunk(
  'resources/updateResources',
  async (resources: ResourceDetailsMutation[], { rejectWithValue }) => {
    try {
      return await updateResourcesAPI(resources);
    } catch (error) {
      return rejectWithValue((error as Error).message || 'Failed to update resources');
    }
  }
);

// Create the slice
const resourcesSlice = createSlice({
  name: 'resources',
  initialState,
  reducers: {
    setSelectedResource: (state, action: PayloadAction<ResourceDetails | null>) => {
      state.selectedResource = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    reset: () => initialState,
    resetFetchStatus: (state) => {
      state.hasAttemptedFetch = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchResources
      .addCase(fetchResources.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchResources.fulfilled, (state, action) => {
        state.isLoading = false;
        state.resources = action.payload;
        state.hasAttemptedFetch = true;
      })
      .addCase(fetchResources.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
        state.hasAttemptedFetch = true;
      })
      // updateResources
      .addCase(updateResources.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateResources.fulfilled, (state) => {
        state.isLoading = false;
        // Note: updateResources returns { success: boolean; message: string }
        // The calling component should dispatch fetchResources to get updated data
      })
      .addCase(updateResources.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

// Export actions
export const { setSelectedResource, clearError, reset, resetFetchStatus } = resourcesSlice.actions;

// Export selectors
export const selectResources = (state: RootState) => state.resources.resources;
export const selectSelectedResource = (state: RootState) => state.resources.selectedResource;
export const selectIsLoading = (state: RootState) => state.resources.isLoading;
export const selectError = (state: RootState) => state.resources.error;
export const selectHasAttemptedFetch = (state: RootState) => state.resources.hasAttemptedFetch;

// Export reducer
export default resourcesSlice.reducer;
